import {
  Contract,
  ErrorMessage,
  ModalDialog,
  Mtds,
  templates,
  useAddContractAnalysisTypeMutation,
  useErrorToast,
  useGetBatchDataQuery,
  widgets
} from 'prove-common'
import { useExistingOrCreateAnalysisType } from '../../hooks/useExistingOrCreateAnalysisType'
import {
  type AnalysisTypeFormData,
  getAnalysisTypeSchema
} from '../../schemas/analysis-type-schema'
import { analysisTypeUiSchema } from '../../schemas/analysis-type-ui-schema'
import { customizeValidator, Form, IChangeEvent } from 'prove-common/rjsf'

type CreateAnalysisTypeCardProps = {
  contract: Contract
  isOpen: boolean
  setOpen: (isOpen: boolean) => void
}

export const CreateAnalysisTypeModal = ({
  contract,
  isOpen,
  setOpen
}: CreateAnalysisTypeCardProps) => {
  const {
    data: batchData,
    isPending: isPendingBatchData,
    isError: isErrorBatchData,
    error: batchDataError
  } = useGetBatchDataQuery(isOpen)
  const sampleTypes = batchData?.sampleTypes
  const analysisTypes = batchData?.analysisTypes
  const existingOrCreateAnalysisType = useExistingOrCreateAnalysisType(
    sampleTypes,
    analysisTypes
  )
  const addContractAnalysisType = useAddContractAnalysisTypeMutation()
  const showError = useErrorToast('legge til analysetypen')

  const onSubmit = async ({
    formData
  }: IChangeEvent<AnalysisTypeFormData>): Promise<void> => {
    if (formData != null) {
      try {
        const analysisType = await existingOrCreateAnalysisType.mutateAsync({
          selectedProductId: formData.productId,
          selectedMatrixId: formData.matrixId,
          selectedSubstanceIds: formData.substanceIds,
          selectedPayloadSchemaId: formData.payloadSchemaId
        })
        // Check if analysisType is already added to contract
        const alreadyInContract =
          contract.analysisTypes?.some(
            (cat) => cat.analysisType?.id === analysisType.id
          ) === true
        if (alreadyInContract) {
          Mtds.toast.info('Analysetypen finnes allerede i kontrakten.')
          return
        }
        await addContractAnalysisType
          .mutateAsync({
            contractId: contract.id,
            body: { analysisTypeId: analysisType.id }
          })
          .then(() =>
            Mtds.toast.success('Analysetypen ble lagt til i kontrakten.')
          )
      } catch (e) {
        showError(e)
      } finally {
        setOpen(false)
      }
    }
  }
  const validator = customizeValidator<AnalysisTypeFormData>()
  const isPending =
    existingOrCreateAnalysisType.isPending || addContractAnalysisType.isPending
  return (
    <ModalDialog
      id='create-analysis-type-modal'
      closedby='any'
      open={isOpen}
      onClose={() => setOpen(false)}
    >
      <Mtds.Button
        command='close'
        commandfor='create-analysis-type-modal'
        aria-label={'Lukk'}
      />
      <Mtds.Prose>
        <Mtds.Heading>Legg til analysetype</Mtds.Heading>
        {isPendingBatchData && <Mtds.Skeleton />}
        {isErrorBatchData && <ErrorMessage error={batchDataError} />}
        {!isPendingBatchData && !isErrorBatchData && (
          <Form<AnalysisTypeFormData>
            noHtml5Validate
            schema={getAnalysisTypeSchema(batchData, contract.labId)}
            uiSchema={analysisTypeUiSchema}
            widgets={widgets}
            templates={templates}
            showErrorList={false}
            validator={validator}
            onSubmit={onSubmit}
            experimental_defaultFormStateBehavior={{
              constAsDefaults: 'skipOneOf'
            }}
          >
            <Mtds.Flex className='mt-8'>
              <Mtds.Button
                command='close'
                commandfor='create-analysis-type-modal'
              >
                Forkast
              </Mtds.Button>
              <Mtds.Button
                type='submit'
                data-variant='primary'
                aria-busy={isPending}
                disabled={isPending}
              >
                Opprett
              </Mtds.Button>
            </Mtds.Flex>
          </Form>
        )}
      </Mtds.Prose>
    </ModalDialog>
  )
}
